import JSZip from 'jszip';
import { SagaIterator } from 'redux-saga';
import { call, takeLatest, put, all, cancelled } from 'redux-saga/effects';
import * as api from '@api/externalUserReports';
import { actions } from './ExternalUserReports.slice';
import {
  ExternalUserReportListFetchAction,
  UploadExternalUserReportAction,
  DownloadExternalUserReportsAction,
  FileDownloadInfo,
  FileUploads,
} from './ExternalUserReports.types';
import { Resp, dispatch } from '../helpers';
import { shortDate } from '@dynamika-shared/utils/date';
import { runToast, updateRunToast, showErrorToast } from '../tools';

const DOWNLOAD_TOAST_AUTO_CLOSE = 120000; // 2 minutes, as ZIP generation can take some time for large files

type FileUploadResult = {
  fileId: string;
  success: boolean;
};

type FileDownloadResult = {
  fileId: string;
  success: boolean;
};

export function* fetchExternalUserReportListWorker(action: ExternalUserReportListFetchAction) {
  try {
    const { clinicalTrialId, patientId } = action.payload;
    const response: Resp<typeof api.fetchExternalUserReportList> = yield call<
      typeof api.fetchExternalUserReportList
    >(api.fetchExternalUserReportList, clinicalTrialId, patientId);
    yield dispatch(actions.fetchExternalUserReportListSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchExternalUserReportListFailure(error as Error));
  }
}

function* uploadWithConcurrencyLimit(
  uploads: FileUploads[],
  files: File[],
  signal: AbortSignal,
  limit: number
) {
  const results: FileUploadResult[] = new Array(uploads.length);
  let nextIndex = 0;

  function* worker(): Generator<unknown, void, unknown> {
    while (true) {
      const currentIndex = nextIndex++;

      if (currentIndex >= uploads.length) {
        return;
      }

      const upload = uploads[currentIndex];
      const file = files[currentIndex];

      try {
        yield call(api.setUploadFile, file, upload.uploadUrl, { signal });

        results[currentIndex] = {
          fileId: upload.fileId,
          success: true,
        };
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw error;
        }

        results[currentIndex] = {
          fileId: upload.fileId,
          success: false,
        };
      }
    }
  }

  const workers = Array.from({ length: limit }, () => call(worker));

  yield all(workers);

  return results;
}

const uniqueZipName = (name: string, usedNames: Set<string>): string => {
  if (!usedNames.has(name)) {
    usedNames.add(name);
    return name;
  }
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex !== -1 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex !== -1 ? name.slice(dotIndex) : '';
  let i = 1;
  let candidate: string;
  do {
    candidate = `${base} (${i})${ext}`;
    i++;
  } while (usedNames.has(candidate));
  usedNames.add(candidate);

  return candidate;
};

function* downloadWithConcurrencyLimit(
  downloadInfos: FileDownloadInfo[],
  files: { id: string; name: string }[],
  zip: JSZip,
  limit: number
) {
  const results: FileDownloadResult[] = new Array(downloadInfos.length);
  const usedNames = new Set<string>();
  let nextIndex = 0;
  let processed = 0;
  const total = downloadInfos.length;

  function* worker(): Generator<unknown, void, unknown> {
    while (true) {
      const currentIndex = nextIndex++;

      if (currentIndex >= total) {
        return;
      }

      const fileId = files[currentIndex]?.id ?? String(currentIndex);

      try {
        const fetchResponse = (yield call(fetch, downloadInfos[currentIndex].url)) as Response;
        const blob = (yield call([fetchResponse, fetchResponse.blob])) as Blob;
        const zipName = uniqueZipName(
          files[currentIndex]?.name ?? `report_${fileId}`,
          usedNames
        );
        zip.file(zipName, blob);
        results[currentIndex] = { fileId, success: true };
      } catch {
        results[currentIndex] = { fileId, success: false };
      }

      ++processed;
      updateRunToast(
        'external-user-download-progress',
        'info',
        `Generating ZIP archive (${processed}/${total} ${total === 1 ? 'file' : 'files'}). Please wait...`,
        undefined,
        { autoClose: processed === total ? false : DOWNLOAD_TOAST_AUTO_CLOSE }
      );
    }
  }

  const workers = Array.from({ length: limit }, () => call(worker));
  yield all(workers);

  return results;
}

export function* uploadExternalUserReportWorker(
  action: UploadExternalUserReportAction
): SagaIterator {
  const controller = new AbortController();
  const signal = controller.signal;

  try {
    const { clinicalTrialId, patientId, siteId, visitId, files } = action.payload;

    const filesToUpload = files.map(({ name, size }) => ({
      fileName: name,
      fileSize: size,
    }));

    const { uploads }: Resp<typeof api.uploadExternalUserReport> = yield call(
      api.uploadExternalUserReport,
      { siteId, clinicalTrialId, visitId, patientId, files: filesToUpload }
    );

    // Upload files with concurrency limit of 5
    const result: FileUploadResult[] = yield call(
      uploadWithConcurrencyLimit,
      uploads,
      files,
      signal,
      5
    );

    const successfulFileIds = result
      .filter(r => r.success)
      .map(r => r.fileId);

    if (successfulFileIds.length === 0) {
      throw new Error('All files failed to upload');
    }

    yield call(api.confirmationExternalUserReport, {
      patientId,
      visitId,
      clinicalTrialId,
      fileIds: successfulFileIds,
    });

    if (successfulFileIds.length !== uploads.length) {
      yield call(
        runToast,
        'warning',
        `Uploaded ${successfulFileIds.length}/${uploads.length} files`
      );
    } else {
      yield call(runToast, 'success', 'All files uploaded successfully');
    }

    yield put(actions.uploadExternalUserReportSuccess(uploads));
    yield put(actions.fetchExternalUserReportList({ clinicalTrialId, patientId }));

  } catch (error) {
    yield put(actions.uploadExternalUserReportFailure(error as Error));
    yield* showErrorToast(error, 'Failed to upload External User Reports');
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

export function* downloadExternalUserReportsWorker(
  action: DownloadExternalUserReportsAction
): SagaIterator {
  const { clinicalTrialId, patientId, visitName, files } = action.payload;
  if (!clinicalTrialId || files.length === 0) return;

  const fileIds = files.map(f => f.id);

  try {
    const downloadInfos: Resp<typeof api.downloadExternalUserFiles> = yield call(
      api.downloadExternalUserFiles,
      clinicalTrialId,
      fileIds
    );

    yield call(runToast, 'info', `Generating ZIP archive (0/${files.length} ${files.length === 1 ? 'file' : 'files'}). Please wait...`, undefined, {
      toastId: 'external-user-download-progress',
      autoClose: DOWNLOAD_TOAST_AUTO_CLOSE,
    });

    const zip = new JSZip();
    const downloadResults: FileDownloadResult[] = yield call(
      downloadWithConcurrencyLimit,
      downloadInfos,
      files,
      zip,
      5
    );

    const failedFiles = downloadResults
      .filter(r => !r.success)
      .map(r => files.find(f => f.id === r.fileId)?.name ?? r.fileId);
    const successCount = files.length - failedFiles.length;

    if (successCount === 0) {
      throw new Error(`All files failed to download: ${failedFiles.join(', ')}`);
    }

    if (failedFiles.length > 0) {
      yield call(
        runToast,
        'warning',
        `Downloaded ${successCount} of ${files.length} files. Failed: ${failedFiles.join(', ')}`
      );
    }

    updateRunToast(
      'external-user-download-progress',
      'info',
      'Compressing ZIP archive. Please wait...',
      undefined,
      { autoClose: DOWNLOAD_TOAST_AUTO_CLOSE }
    );

    const zipBlob: Blob = yield call([zip, zip.generateAsync], {
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });

    updateRunToast(
      'external-user-download-progress',
      'success',
      'Download is starting...',
      undefined,
      { autoClose: 1000 }
    );

    const blobUrl = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${patientId}_${visitName}_reports_${shortDate(new Date())}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    yield* showErrorToast(error, 'Failed to download files');
  }
}

export function* ExternalUserReportsSaga() {
  yield takeLatest(
    actions.fetchExternalUserReportList.type,
    fetchExternalUserReportListWorker
  );
  yield takeLatest(
    actions.uploadExternalUserReport.type,
    uploadExternalUserReportWorker
  );
  yield takeLatest(
    actions.downloadExternalUserReports.type,
    downloadExternalUserReportsWorker
  );
}
